import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { verifyEmail, resendVerification } from "../../../Store/userSlice";
import logo from "../../../assets/images/logo.png";
import "./VerifyEmail.css";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [countdown, setCountdown] = useState(3);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | "sent" | "already_verified"

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    dispatch(verifyEmail(token)).then((action) => {
      if (action.payload?.success) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    });
  }, [token, dispatch]);

  // Auto-redirect to home after success
  useEffect(() => {
    if (status !== "success") return;
    if (countdown <= 0) {
      navigate("/");
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim() || resendLoading) return;

    setResendLoading(true);
    setResendStatus(null);
    try {
      const action = await dispatch(resendVerification(resendEmail.trim()));
      const payload = action.payload;
      if (payload?.success && payload?.data?.alreadyVerified) {
        setResendStatus("already_verified");
      } else {
        setResendStatus("sent");
      }
    } catch {
      setResendStatus("sent"); // neutral fallback
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">
        <NavLink to="/">
          <img src={logo} alt="Casa del Padel" className="verify-email-logo" />
        </NavLink>

        {status === "loading" && (
          <div className="verify-email-loading">
            <div className="verify-email-spinner" />
            <h2 className="verify-email-title">Vérification en cours…</h2>
            <p className="verify-email-subtitle">
              Nous vérifions votre lien, veuillez patienter.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="verify-email-success">
            <div className="verify-email-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#dcfce7" />
                <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="verify-email-title">E-mail confirmé !</h2>
            <p className="verify-email-subtitle">
              Votre compte est actif. Vous allez être redirigé vers l'application dans{" "}
              <strong>{countdown}s</strong>…
            </p>
            <button className="verify-email-btn" onClick={() => navigate("/")}>
              Accéder maintenant
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="verify-email-error-state">
            <div className="verify-email-icon verify-email-icon--error">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#fee2e2" />
                <path d="M15 9l-6 6M9 9l6 6" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="verify-email-title">Lien invalide ou expiré</h2>
            <p className="verify-email-subtitle">
              Ce lien de vérification n'est plus valide (durée de vie 24h).
              Saisissez votre adresse e-mail pour en recevoir un nouveau.
            </p>

            {resendStatus === "sent" && (
              <div className="verify-email-resend-success">
                Si votre adresse est enregistrée et non vérifiée, un nouveau lien vient d'être envoyé. Vérifiez aussi vos spams.
              </div>
            )}

            {resendStatus === "already_verified" && (
              <div className="verify-email-already-verified">
                Ce compte est déjà vérifié.{" "}
                <NavLink to="/login" className="verify-email-back-link" style={{ display: "inline" }}>
                  Connectez-vous
                </NavLink>
              </div>
            )}

            {resendStatus === null && (
              <form className="verify-email-resend-form" onSubmit={handleResend}>
                <input
                  type="email"
                  className="verify-email-input"
                  placeholder="votre@email.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
                <button
                  type="submit"
                  className="verify-email-btn verify-email-btn--outline"
                  disabled={resendLoading}
                >
                  {resendLoading ? "Envoi en cours…" : "Renvoyer le lien"}
                </button>
              </form>
            )}

            <NavLink to="/login" className="verify-email-back-link">
              Retour à la connexion
            </NavLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
